import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import type { FeedbackItem, StageRef } from '../../types/taActionBoard';
import { ModalActions } from './ModalActions';

/**
 * Moving a candidate to the OFFER stage.
 *
 * Deliberately not the invite-email flow: there is no interview to book and
 * nothing to draft, because the offer itself is assembled in Lever. So this is
 * a plain confirmation that moves the stage and opens the candidate's Lever
 * profile, where the offer details go.
 */
export function OfferMoveBody({
  item,
  options,
  stageId,
  onStageId,
  targetName,
  onConfirm,
  onCancel,
}: {
  item: FeedbackItem;
  options: StageRef[];
  stageId: string;
  onStageId: (id: string) => void;
  targetName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const candidateFirstName = item.name.trim().split(/\s+/)[0] || item.name;
  return (
    <>
      <ModalHeader>
        <ModalTitle>
          Move {item.name} to {targetName}?
        </ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          {candidateFirstName} moves to “{targetName}” in Lever. No email is drafted here — the
          offer itself is put together in Lever.
        </p>
        <p className="text-sm text-gray-600">
          Their Lever profile opens in a new tab so you can enter the offer details right away.
        </p>
        {options.length > 1 && (
          <div className="min-w-[14rem]">
            <Select value={stageId} onValueChange={onStageId}>
              <SelectTrigger label="Move to stage" className="w-full">
                <SelectValue placeholder="Choose a stage" />
              </SelectTrigger>
              <SelectContent>
                {options.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.id === item.nextStageId ? ' (suggested)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <ModalActions
        confirmLabel="📝 Move & open Lever"
        confirmDisabled={!stageId}
        onConfirm={() => {
          // Opened from the click handler so the browser treats it as
          // user-initiated; a popup blocker would otherwise swallow it.
          window.open(item.leverUrl, '_blank', 'noopener,noreferrer');
          onConfirm();
        }}
        onCancel={onCancel}
      />
    </>
  );
}
